(ns practitest.frontend.issue.bugtracker
  (:require [practitest.frontend.components.modal :as modal]
            [practitest.frontend.components2.spinners :as spinners2]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.entity-form :as entity-form]
            [re-frame.core :as re-frame]
            [practitest.frontend.issue.common :refer [issue-form-id]]
            [practitest.frontend.common-entities.metadata :as metadata :refer [entities-metadata]]
            [ajax.core :as ajax]
            [clojure.string :as str]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [practitest.frontend.components2.entity-form.forms :as forms]
            [practitest.frontend.forms3.entity-form.forms :as forms3]
            [practitest.frontend.forms3.entity-form.fields :as fields3]
            [practitest.frontend.components2.entity-form.fields :as fields]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.common.tooltip :as common-tooltip]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.common.permissions :as permissions]
            [practitest.frontend.common.bugtrackers :as bugtrackers]
            [practitest.frontend.common.keyboard-shortcuts :as keyboard-shortcuts]
            [practitest.frontend.common.re-frame :refer [partial-dispatch
                                                         partial-dispatch-sync
                                                         partial-subscribe]]
            [practitest.frontend.common.i18n :refer [format-translation get-translation] :as i18n]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.components.grid.localstorage :as grid-storage]
            [practitest.frontend.components.selector :as selector2]
            [practitest.frontend.components.tooltip :as tooltip]
            [reagent.core :as reagent]
            [reagent.dom :as reagent-dom]
            [practitest.frontend.impersonation :as impersonation]))

;; Bugtracker (jira, ClickUp and AzureDevops for now) integration for Issues and Run(ET test) screen

;; Separate form id for bugtracker edit modal
(def bugtracker-form-id :issue-bugtracker)

(defn bugtracker-fields
  [all-fields]
  (filter :bugtracker (vals all-fields)))

(re-frame/reg-sub
 ::bugtracker-fields
 (fn []
   (re-frame/subscribe [::subs/fields]))
 (fn [all-fields]
   (bugtracker-fields all-fields)))

;; We assume that only one bugtracker can be active at a time
(defn bugtracker-type [current-project]
  (:integrated_bugtracker current-project))

(defn get-bugtracker-field-value
  [db field-id]
  (get-in db [:form-states2 bugtracker-form-id :fields field-id :value]))

(re-frame/reg-sub
 ::get-bugtracker-field-value
 (fn [db [_ value]]
   (get-bugtracker-field-value db value)))

(re-frame/reg-sub
 ::get-entity-types-for-list
 (fn [db [_ list-id]]
   (let [current-project (subs/get-current-project db)
         bugtracker-raw (bugtracker-type current-project)
         bugtracker-name (when bugtracker-raw
                           (let [bugtracker-keyword (keyword (str/lower-case bugtracker-raw))]
                             (if (= bugtracker-keyword :clickup) :click-up bugtracker-keyword)))
         bugtracker-data (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data])
         entity-types-obj (when bugtracker-data (get bugtracker-data :entity_types))
         ;; Convert string list-id to keyword to match the map keys
         entity-types (when (and (= bugtracker-name :click-up) list-id entity-types-obj)
                        (get entity-types-obj (keyword list-id)))]
     entity-types)))

(re-frame/reg-sub
 ::clickup-sync-all-lists?
 (fn [db]
   (boolean (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data :sync_all_lists]))))

(re-frame/reg-sub
 ::get-clickup-lists-for-space
 (fn [db [_ space-id]]
   (let [bugtracker-data (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data])
         lists-by-space (:lists_by_space bugtracker-data)]
     (when (and space-id lists-by-space)
       (get lists-by-space (keyword space-id))))))

(re-frame/reg-sub
 ::get-work-item-types-for-azure-project
 (fn [db [_ project-id]]
   (let [bugtracker-data (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data])
         wits-by-project (:azure_work_item_types bugtracker-data)]
     (when (and project-id wits-by-project)
       (get wits-by-project (keyword project-id))))))

(re-frame/reg-sub
 ::get-issue-types-for-jira-project
 (fn [db [_ jira-project-id]]
   (let [bugtracker-data (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data])
         jira-options (:jira_options bugtracker-data)]
     (when (and jira-project-id jira-options)
       ;; Filter jira_options to get all issue types for the selected project
       (->> jira-options
            (filter #(and (= (:p_id %) jira-project-id)
                          (not (:entity_disabled %))))
            (map (fn [option]
                   {:issue_type_id (:issue_type_id option)
                    :issue_name (:issue_name option)
                    :is_sub_task (:is_sub_task option)}))
            (distinct))))))

(re-frame/reg-sub
 ::jira-selected-project-id
 (fn [db]
   (let [selected-project (get-bugtracker-field-value db :jira-project)]
     (:p_id selected-project))))

(re-frame/reg-sub
 ::jira-parent-task-visible?
 (fn [db]
   (let [selected-issue-type (get-bugtracker-field-value db :jira-issue-type)]
     (= "true" (:is_sub_task selected-issue-type)))))

(defn- get-et-annotation-attachments [db et-annotation-id]
  (let [et-annotation (first (filter #(= (:id %) et-annotation-id)
                                     (get-in db [:et-state :entity :annotations])))]
    (:attachments et-annotation)))

(defn- get-step-run-attachments [db step-run-id]
  (get-in db [:entity :step-run step-run-id :attachments :data]))

(re-frame/reg-event-db
 ::init-create-issue-form
 (fn [db [_ {:keys [issue-name issue-description traced-requirements params on-success-events]}]]
   (-> db
       ;; Clear form state
       (assoc-in [:form-states2 bugtracker-form-id :fields] {})
       ;; Copy name and description
       (assoc-in [:form-states2 bugtracker-form-id :fields :jira-issue-name :value] issue-name)
       (assoc-in [:form-states2 bugtracker-form-id :fields :jira-issue-description :value] issue-description)
       ;; Initialize ClickUp fields
       (assoc-in [:form-states2 bugtracker-form-id :fields :clickup-parent-task :value] nil)
       (assoc-in [:form-states2 bugtracker-form-id :fields :clickup-entity-type :value] nil)
       ;; Initialize Azure fields
       (assoc-in [:form-states2 bugtracker-form-id :fields :azure-work-item-type :value] nil)
       (assoc-in [:form-states2 bugtracker-form-id :fields :azure-parent-id :value] nil)
       ;; Initialize Jira split fields
       (assoc-in [:form-states2 bugtracker-form-id :fields :jira-issue-type :value] nil)
       (assoc-in [:form-states2 bugtracker-form-id :fields :jira-parent-task :value] nil)
       ;; Traced requirement - default select when there's exactly one
       (assoc-in [:form-states2 bugtracker-form-id :fields :requirement-id :value]
                 (when (= (count traced-requirements) 1) (first traced-requirements)))
       (assoc-in [:form-states2 bugtracker-form-id :bugtracker-modal :traced-requirements] traced-requirements)
       (assoc-in [:form-states2 bugtracker-form-id :params] params)
       (assoc-in [:form-states2 bugtracker-form-id :on-success-events] on-success-events)
       (assoc-in [:form-states2 bugtracker-form-id :bugtracker-modal :attachments-panel :attachments-to-show]
                 (cond (:et_annotation_id params) (get-et-annotation-attachments db (:et_annotation_id params))
                       (:step_run_id params) (get-step-run-attachments db (:step_run_id params))
                       :else [])))))

(defn icons-map-by-bugtracker [bugtracker]
  (cond
    (#{:jiracloud :jiraserver :jiraservernp} bugtracker) icons/mux-jira-icon
    (= bugtracker :clickup) icons/mux-clickup-icon
    (= bugtracker :youtrack) icons/mux-youtrack-icon
    (= bugtracker :azuredevops) icons/mux-azure-icon))

(defn bugtracker-logo []
  (let [bugtracker (bugtracker-type @(re-frame/subscribe [::subs/current-project]))
        bugtracker-keyword (keyword (str/lower-case bugtracker))
        icon (icons-map-by-bugtracker bugtracker-keyword)]
    [:span.external-system-logo
     (when icon
       [icon])]))

(defn filter-entity-bugtracker-fields [bugtracker-fields entity-type]
  (filter #(entity-type (:bugtracker %)) bugtracker-fields))

(defn sort-bugtracker-fields
  "Order bugtracker fields the same way every other section does - by the
   per-entity FieldEntity#position (falling back to :order for ties/missing values)."
  [bugtracker-fields entity-fields]
  (sort-by (fn [field]
             (let [entity-field (get entity-fields (keyword (:name field)))]
               [(:position entity-field) (:order entity-field)]))
           bugtracker-fields))

(defn additional-fields-form3
  [_]
  (let [expanded? (reagent/atom false)  ; Create state with r/atom
        initial-visible-items 2
        bugtracker (bugtracker-type @(re-frame/subscribe [::subs/current-project]))
        section-collapsed? (reagent/atom nil)]
    (fn [{:keys [entity-type] :as form-props}]  ; Return render function to properly handle reactivity
      (let [_ (when (nil? @section-collapsed?)
                (reset! section-collapsed?
                        (grid-storage/get-section-collapsed entity-type "integration" false)))
            bugtracker-fields @(re-frame/subscribe [::bugtracker-fields])
            entity-fields @(re-frame/subscribe [::subs/entity-fields entity-type])
            entity-bugtracker-fields (sort-bugtracker-fields
                                      (filter-entity-bugtracker-fields bugtracker-fields entity-type)
                                      entity-fields)
            visible-fields (if @expanded?  ; Deref the atom here
                             entity-bugtracker-fields
                             (take initial-visible-items entity-bugtracker-fields))]
        (when (seq entity-bugtracker-fields)
          [:div.g2-lane-section.new-form-bugtracker-section
           {:class (dom/classes-pairs-v :section-collapsed @section-collapsed?)}
           [:div.bugtracker-title-section
            [buttons/icon-button
             {:icon (if @section-collapsed?
                      [icons/mux-chevron-right]
                      [icons/mux-chevron-down2])
              :on-click #(let [new-state (not @section-collapsed?)]
                           (reset! section-collapsed? new-state)
                           (grid-storage/set-section-collapsed entity-type "integration" new-state))}]
            [:div.bugtracker-title
             [:span.section-label (format-translation
                                    (get-translation "entity.bugtracker.additional-fields.label"
                                                     "%{bugtracker} Fields")
                                    {:bugtracker (bugtrackers/get-label bugtracker)})]
             [:span.bugtracker-info
              [common-tooltip/info-circle-tooltip
               (format-translation
                (get-translation "entity.bugtracker.additional-fields.info"
                                 "Editing fields in this section updates corresponding information in %{bugtracker}")
                {:bugtracker (bugtrackers/get-label bugtracker)})]]]

            [:div.header-controls
             (when (permissions/has-permission? "issues_editor")
               [buttons/button
                {:tertiary? true
                 :icon [icons/mux-pencil]
                 :text (get-translation "entity.form.edit-fields" "Edit Fields")
                 :on-click (fn [_]
                             (mixpanel/track "Form Actions" {"Action" "Edit Integration Fields"})
                             (re-frame/dispatch [::show-bugtracker-modal! :edit]))}])]]

           (when-not @section-collapsed?
             [:<>
              ;; Readonly bugtracker fields
              (into [:div.g2-fields-section.wrap]
                    (map (fn [field]
                           (with-meta [fields/build-field
                                       {:field-name (:name field)
                                        :edit-permissions? false}
                                       form-props]
                             {:key (:name field)}))
                         visible-fields))

              ;; Show expand/collapse button only if there are more than 2 fields
              (when (> (count entity-bugtracker-fields) initial-visible-items)
                [:div.integration.toggle-expand
                 [:hr {:class "toggle-expand-line"}]
                 [:span.text
                  {:on-click (fn [e]
                               (.preventDefault e)
                               (swap! expanded? not))}  ; Toggle the expanded state
                  (if @expanded?  ; Deref the atom here too
                    (get-translation "entity.form.view-less" "View Less")
                    (get-translation "entity.form.view-more" "View More"))]])])])))))

(re-frame/reg-sub
 ::show-bugtracker-modal?
 (fn []
   (re-frame/subscribe [::forms/form-state bugtracker-form-id]))
 (fn [form-state _]
   (get-in form-state [:bugtracker-modal :show?])))

(re-frame/reg-sub
 ::bugtracker-modal-mode
 (fn []
   (re-frame/subscribe [::forms/form-state bugtracker-form-id]))
 (fn [form-state _]
   (or (get-in form-state [:bugtracker-modal :mode])
       :edit)))

(re-frame/reg-sub
 ::traced-requirements
 (fn []
   (re-frame/subscribe [::forms/form-state bugtracker-form-id]))
 (fn [form-state _]
   (get-in form-state [:bugtracker-modal :traced-requirements])))

(re-frame/reg-sub
 ::create-modal-params
 (fn []
   (re-frame/subscribe [::forms/form-state bugtracker-form-id]))
 (fn [form-state _]
   (:params form-state)))

(defn- show-bugtracker-modal!
  "Set bugtracker modal init-data and open it. Accept params:\n
   - mode: can be :edit or :create\n
   - init-data: set init data for :create mode modal.\n:edit mode does not use this param.\n
   Signature: {:issue-name \"Some name\" :issue-description \"Some description\" :params {:entity_id 123}
               :on-success-events [[::some-event arg1]]}\n
   :on-success-events is an optional vector of partial re-frame event vectors. Each one is dispatched
   in ::bugtracker-create-success, with the created issue entity conj'd on as the final arg, before the
   modal is hidden."
  [{db :db} [_ mode init-data]]
  (let [current-entity (:current-entity db)
        default-bugtracker-fields (map :name (bugtracker-fields (:fields db)))]
    {:db (-> db
             (assoc-in [:form-states2 bugtracker-form-id :bugtracker-modal :mode] mode))
     :dispatch-n (case mode
                   :edit
                   [[::forms/reset-form-state false current-entity bugtracker-form-id default-bugtracker-fields]
                    [::fetch-bugtracker-fields-info]]

                   :create
                   [[::init-create-issue-form init-data]
                    [::fetch-bugtracker-create-info (:project init-data)]])}))

(re-frame/reg-event-fx ::show-bugtracker-modal! show-bugtracker-modal!)

(defn set-modal-error-panel! [db show response-error]
  (-> db
      (assoc-in [:form-states2 bugtracker-form-id :bugtracker-modal :error-panel :show-error-panel] show)
      (assoc-in [:form-states2 bugtracker-form-id :bugtracker-modal :error-panel :response-error] response-error)))

(defn- clear-bugtracker-modal-cache [db]
  (assoc-in db [:form-states2 bugtracker-form-id :bugtracker-cached-data] nil))

(re-frame/reg-event-db
 ::hide-bugtracker-modal!
 (fn [db]
   (-> db
       (assoc-in [:form-states2 bugtracker-form-id :bugtracker-modal :show?] false)
       ;;close and clear error panel
       (set-modal-error-panel! false nil)
       (clear-bugtracker-modal-cache)
       ;; Clear form fields and bugtracker data so project resets on next open
       (assoc-in [:form-states2 bugtracker-form-id :fields] {})
       (assoc-in [:form-states2 bugtracker-form-id :bugtracker-fields-data] nil))))

(re-frame/reg-event-fx
 ::fetch-bugtracker-fields-info
 (fn [{db :db}]
   {:db (update db :progress conj ::fetch-bugtracker-fields-info)
    :http-xhrio
    (let [current-entity (:current-entity db)
          entity-fields (:entity-fields db)
          bugtracker-fields (bugtracker-fields (:fields db))
          entity-bugtracker-fields (filter-entity-bugtracker-fields bugtracker-fields current-entity)
          jira? (bugtrackers/jira? (bugtracker-type (subs/get-current-project db)))]
      {:method          :get
       :uri             (broutes/get-url (if jira?
                                           "jira_modal#jira_update_modal_body"
                                           "bugtracker_modal#bugtracker_update_modal_body")
                                         {:p_id       (subs/get-current-project-id db)
                                          :project_id (subs/get-current-project-id db)})
       :vec-strategy    :rails
       :params          {;; We need entity_field id's here, not sure why
                         :cfes               (keep #(get-in entity-fields [current-entity (:name %1) :field_entity_id])
                                                   (filter :custom? entity-bugtracker-fields))
                         ;; For system fields it's just field ids
                         :sfs                (map :id (filter :system? entity-bugtracker-fields))
                         :customizable       (subs/get-current-entity-id db current-entity)
                         ;; issue for now
                         :customizable_class (if (= current-entity :issue) "Issue" "Requirement")}
       :format          (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success      [::got-bugtracker-fields-info]
       :on-failure      [::fetch-bugtracker-fields-error]})}))

(re-frame/reg-event-fx
 ::fetch-bugtracker-create-info
 (fn [{db :db} [_ project]]
   (let [jira? (bugtrackers/jira? (bugtracker-type (subs/get-current-project db)))
         current-entity (:current-entity db)
         entity-type (if (= current-entity :requirement) "Requirement" "Issue")]
     {:db (update db :progress conj ::fetch-bugtracker-fields-info)
      :http-xhrio
      {:method          :get
       :uri             (broutes/get-url (cond
                                           (and jira? (some? project))       "integration_settings#jira_project_fields"
                                           (and (not jira?) (some? project)) "bugtracker_settings#bugtracker_project_fields"
                                           jira?                             "integration_settings#jira_projects"
                                           :else                             "bugtracker_settings#bugtracker_projects")
                                         {:project_id (subs/get-current-project-id db)})
       :vec-strategy    :rails
       :params          (cond-> {}
                          (and (some? project) jira?)
                          (assoc :jira_pid (:p_id project)
                                 :issue_type_id (:issue_type_id project))

                          (and (some? project) (not jira?))
                          (assoc :bugtracker_pid (:id project))

                          jira?
                          (assoc :entity_type entity-type))
       :format          (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success      [::got-create-fields-info jira?]
       :on-failure      [::fetch-bugtracker-fields-error]}})))

(defn get-field-config
  [jira-data current-jira-project]
  (let [current-project-keyword (str (:p_id current-jira-project) " - " (:issue_type_id current-jira-project))]
    ;; Yep, it's awful
    (get-in jira-data [:jira_fields :proj_req_flds (keyword current-project-keyword)])))

(defn get-jira-required-fields
  [jira-data]
  (let [current-jira-project (:jira_project jira-data)
        integrated-fields (:integrated_fields jira-data)
        project-fields (:requiredfields (get-field-config jira-data current-jira-project))
        filtered-fields (filter #(or (contains? (set integrated-fields) (:key %))
                                     (:required %))
                                project-fields)]
    (->> filtered-fields
         (remove
          (fn [field]
            ;; Skip this special field from jira
            (and (= "issuelinks" (:key field))
                 (nil? (:allowedValues field)))))
         (sort-by :name))))

(defn get-bugtracker-field-errors
  [jira? response]
  (let [fields (if jira?
                (:requiredfields (get-field-config response (:jira_project response)))
                (:proj_req_flds (:bugtracker_fields response)))]
    (filter :error fields)))

(defn assoc-field-errors
  [fields field-errors field-id-key]
  (reduce
   (fn [fields field]
     (let [field-id (keyword (field-id-key field))]
       (-> fields
           (assoc-in [field-id :value] (:value field))
           (assoc-in [field-id :errors] [{:message (:error field)}]))))
   fields
   field-errors))

(defn get-jira-field-by [jira-data field-name by-field]
  (let [current-jira-project (:jira_project jira-data)
        required-project-fields (:requiredfields (get-field-config jira-data current-jira-project))
        filtered-fields (filterv (fn [value] (= (by-field value) field-name)) required-project-fields)]
    (first filtered-fields)))

(defn get-jira-field-possible-values [jira-data jira-field-type allowedValues key]
  (case jira-field-type
    :epic
    (map #(vector (:name %) (:id %)) allowedValues)
    (:list :multilist)
    (map (fn [item]
           (if (string? item)
             ;; Special case for labels field
             [item item]
             ;; It can be either :name or :value field
             (let [value (or (:name item)
                             (:value item))]
               [value value])))
         allowedValues)

    :user
    (get-in jira-data [:users (keyword key)])

    :sprint
    (get-in jira-data [:sprints (keyword key)])

    :securitylevel
    (get-in jira-data [:security_levels (keyword key)])

    :team
    (get-in jira-data [:teams (keyword key)])
    allowedValues))

(defn get-bugtracker-required-fields
  [bugtracker-data]
  (let [integrated-fields (:integrated_fields bugtracker-data)
        project-fields (:proj_req_flds (:bugtracker_fields bugtracker-data))
        filtered-fields (filter #(and (or (not (:disabled %)) (some? (:error %)))
                                      (or (contains? (set integrated-fields) (:id %))
                                          (:required %)))
                                project-fields)]            ; filter from all fields, excluding read-only fields
    filtered-fields))

(defn get-jira-field-type
  [jira-field]
  (let [schema-type (get-in jira-field [:schema :type])
        key (:key jira-field)
        multi? (= "array" schema-type)
        list-field? (contains? jira-field :allowedValues)
        sprint? (= "Sprint" (:name jira-field))
        securitylevel? (= "Security Level" (:name jira-field))
        user? (= "user" schema-type)
        team? (= "team" schema-type)
        date? (= "date" schema-type)]
    (cond
      (= "issuelinks" key)
      [:issuelinks :list]

      sprint?
      [:sprint :list]

      securitylevel?
      [:securitylevel :list]

      user?
      [:user :jira_user_list]

      team?
      [:team :list]

      date?
      [:date :date]

      list-field?
      (cond multi?
            [:multilist :multilist]
            (= (:name jira-field) "Epic Link")
            [:epic :list]
            :else
            [:list :list])

      :else
      [:text :text])))

(defn- process-jira-default-values
  [response]
  (reduce
   (fn [acc field]
     (let [[jira-field-type pt-field-type] (get-jira-field-type field)]
       (if (:hasDefaultValue field)
         (assoc acc (:key field)
                (cond
                  ;; Special handling for version and priority fields - don't know why
                  (#{"priority" "version"} (:key field))
                  [(get-in field [:defaultValue :name]) (get-in field [:defaultValue :name])]

                  (= :list pt-field-type)
                  [(get-in field [:defaultValue :name]) (get-in field [:defaultValue :id])]

                  (= :multilist pt-field-type)
                  #{[(get-in field [:defaultValue :name]) (get-in field [:defaultValue :id])]}

                  :else
                  (get-in field [:defaultValue :name])))
         acc)))
   {}
   (get-jira-required-fields response)))

(re-frame/reg-event-fx
 ::extend-fields
 (fn [{db :db} []]
   (let [extra-fields (->> @(re-frame/subscribe [::bugtracker-fields-config])
                           (filter (fn [field] (not (#{:jira-issue-name :jira-issue-description} (:field-name field)))))
                           (reduce (fn [sum {:keys [field-name]}] (merge sum {(keyword field-name) {:value nil}})) {}))]
     {:db (-> db
              ;; remove all prev fields except base fields and Jira split fields
              (update-in [:form-states2 bugtracker-form-id :fields]
                        (fn [form-fields]
                          (select-keys form-fields [:jira-issue-name :jira-issue-description
                                                    :jira-project :jira-issue-type :jira-parent-task
                                                    :requirement-id])))
              ;; add new extra fields
              (update-in [:form-states2 bugtracker-form-id :fields] (fn [form-fields] (merge form-fields extra-fields))))})))

(re-frame/reg-event-fx
 ::apply-bugtracker-field-errors
 (fn [{db :db} [_ field-errors field-id-key]]
   {:db (update-in db [:form-states2 bugtracker-form-id :fields]
                   #(assoc-field-errors % field-errors field-id-key))}))

(re-frame/reg-event-fx
 ::got-create-fields-info
 (fn [{db :db} [_ jira? response]]
   (let [errors (:errors response)
         field-errors (get-bugtracker-field-errors jira? response)
         field-id-key (if jira? :key :id)
         default-values (if jira?
                          (process-jira-default-values response))
         mode (or (get-in db [:form-states2 bugtracker-form-id :bugtracker-modal :mode]) :edit)
         ;; For Jira in create mode, check if there's a pre-selected project
         jira-project (when (and jira? (= mode :create)) (:jira_project response))]

     {:db         (cond->
                   db

                    true
                    (update :progress disj ::fetch-bugtracker-fields-info)

                    ;; No errors case — show modal now that we know the user is linked
                    (empty? errors)
                    (-> (assoc-in [:form-states2 bugtracker-form-id :bugtracker-modal :show?] true)
                        (update-in [:form-states2 bugtracker-form-id :bugtracker-fields-data]
                                   merge
                                   response)
                        ;; Init default fields on form
                        (update-in [:form-states2 bugtracker-form-id :fields]
                                   (fn [form-fields]
                                     (reduce-kv
                                      (fn [acc key value]
                                        (assoc-in acc [key :value] value))
                                      form-fields
                                      default-values)))
                        ;; For Jira create mode: if there's a pre-selected project, store it in form field
                        (cond->
                          jira-project
                          (-> (assoc-in [:form-states2 bugtracker-form-id :fields :jira-project :value] jira-project)
                              (assoc-in [:form-states2 bugtracker-form-id :fields :jira-project :dirty?] false)))))

      :dispatch-n (let [authorize-url (:authorize_url response)
                        integ-name    (bugtrackers/get-label (bugtracker-type (subs/get-current-project db)))
                        ;; Fetch default project fields for non-Jira integrations
                        default-project-fetch (when (and (empty? errors) (not jira?) (= mode :create)
                                                         (:bugtracker_project response))
                                                [[::fetch-bugtracker-create-info (:bugtracker_project response)]])]
                    (into (cond
                           (and (seq errors) (not-empty authorize-url))
                           [[::impersonation/show {:integration-name      integ-name
                                                   :authorize-url          authorize-url
                                                   :personal-settings-url (broutes/get-url-view "personals#index")}]]

                           (seq errors)
                           [[::notifications/error2 (str/join "; " errors)]]

                           (seq field-errors)
                           [[::extend-fields] [::apply-bugtracker-field-errors field-errors field-id-key]]

                           :else
                           [[::extend-fields]])
                          default-project-fetch))})))

(re-frame/reg-event-fx
 ::fetch-bugtracker-fields-error
 (fn [{db :db} _]
   {:db (-> db
            (update :progress disj ::fetch-bugtracker-fields-info))

    :dispatch-n [[::hide-bugtracker-modal!]
                 [::notifications/error2 (format-translation
                                          (get-translation "entity.bugtracker.fetch-bugtracker-fields-error"
                                                           "Error fetching %{bugtracker} fields")
                                          {:bugtracker (bugtrackers/get-label
                                                        (bugtracker-type
                                                         (:current-project db)))})]]}))

(re-frame/reg-event-fx
 ::got-bugtracker-fields-info
 (fn [{db :db} [_ response]]
   (let [errors      (:errors response)
         field-errors (filter :error (:data response))
         authorize-url (:authorize_url response)
         integ-name    (bugtrackers/get-label (bugtracker-type (subs/get-current-project db)))]
     (cond-> {:db (cond->
                   db

                    true
                    (update :progress disj ::fetch-bugtracker-fields-info)

                    ;; No errors case — show modal now that we know the user is linked
                    (empty? errors)
                    (-> (assoc-in [:form-states2 bugtracker-form-id :bugtracker-modal :show?] true)
                        (assoc-in [:form-states2 bugtracker-form-id :bugtracker-fields-data] response))

                    ;; Field errors case - populate form-state with errors (and invalid values)
                    (seq field-errors)
                    (update-in [:form-states2 bugtracker-form-id :fields]
                               #(assoc-field-errors % field-errors :id))

                    (and (seq errors) (not authorize-url))
                    (-> (assoc-in [:form-states2 bugtracker-form-id :bugtracker-fields-error] (str/join ";" errors))
                        (set-modal-error-panel! true (str/join ";" errors))))}

       (and (seq errors) authorize-url)
       (assoc :dispatch-n [[::hide-bugtracker-modal!]
                           [::impersonation/show {:integration-name      integ-name
                                                  :authorize-url          authorize-url
                                                  :personal-settings-url (broutes/get-url-view "personals#index")}]])))))

(defn get-bugtracker-fields-info
  [db]
  (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data]))

(defn get-bugtracker-fields-errors
  [db]
  (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-error]))

(re-frame/reg-sub
 ::bugtracker-fields-errors
 (fn [db]
   (get-bugtracker-fields-errors db)))

(re-frame/reg-sub
 ::bugtracker-fields-info
 (fn [db]
   (get-bugtracker-fields-info db)))

(def bugtracker-form-props
  {:get-field-props (partial-subscribe [::bugtracker-field-props])
   :get-form-state  (partial-subscribe [::forms/form-state bugtracker-form-id])
   :set-form-state  (partial-dispatch [::forms/set-form-state bugtracker-form-id])

   :get-field-state (partial-subscribe [::forms/field-state bugtracker-form-id])
   :set-field-state (partial-dispatch-sync [::forms/set-field-state bugtracker-form-id])

   :form-ref (atom nil)})

(defn get-bugtracker-name []
  (let [bugtracker (bugtracker-type @(re-frame/subscribe [::subs/current-project]))
        bugtracker-keyword (keyword (str/lower-case bugtracker))]
    (cond
      (#{:jiracloud :jiraserver :jiraservernp} bugtracker-keyword) :jira
      (= bugtracker-keyword :clickup) :click-up
      (= bugtracker-keyword :azuredevops) :azuredevops
      (= bugtracker-keyword :youtrack) :youtrack
      :else "Unsupported Bugtracker")))

(defn- get-project-label
  ([] (get-project-label false))
  ([clickup-sync-all-lists?]
   (let [bugtracker-name (get-bugtracker-name)]
     (case bugtracker-name
       :jira "Project"
       :click-up (if clickup-sync-all-lists? "ClickUp Space" "ClickUp Space and List")
       :azuredevops "Azure Project"
       :youtrack "YouTrack Project"))))

(defn- get-bugtracker-related-fields [bugtracker-data]
  (let [bugtracker-name  (get-bugtracker-name)
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        ;; In create mode with split fields, check selected issue type from form state
        ;; In edit mode, check from backend data (jira_project)
        selected-issue-type @(re-frame/subscribe [::get-bugtracker-field-value :jira-issue-type])
        subtask? (when (= :jira bugtracker-name)
                   (if (= mode :create)
                     (= "true" (:is_sub_task selected-issue-type))
                     (= "true" (-> bugtracker-data :jira_project :is_sub_task))))]
    (if (= :jira bugtracker-name)
      {:subtask? subtask?
       :supports-linked-issue? true}
      {:subtask? false :supports-linked-issue? false})))

(defn- normalize-jira-data [{:keys [bugtracker-data current-bugtracker-project]}]
  (map (fn [jira-field]
         (let [{:keys [key name required allowedValues]} jira-field
               [jira-field-type pt-field-type] (get-jira-field-type jira-field)
               jira-data (get-field-config bugtracker-data current-bugtracker-project)
               possible_values (get-jira-field-possible-values jira-data jira-field-type allowedValues key)]
           {:id (keyword key)
            :name name
            :required required
            :possible_values possible_values
            :field_format (keyword pt-field-type)}))
       (get-jira-required-fields bugtracker-data)))

(defn- get-bugtracker-mapped-fields-config [{:keys [bugtracker-name bugtracker-data current-bugtracker-project]}]
  (let [mode @(re-frame/subscribe [::bugtracker-modal-mode])]
    (if (= bugtracker-name :jira)
      (map (fn [{:keys [id name required possible_values field_format]}]
             (let [id (if (keyword? id) id (keyword id))
                   field_format (if (keyword? field_format) field_format (keyword field_format))]
               {:field-name        id
                :label             name
                :mandatory?        required
                :validators        (when required (forms/get-mandatory-validator field_format))
                :type              (keyword field_format)
                :possible-values   (if (= mode :create)
                                     possible_values
                                     (map (fn [value] (if (vector? value) (first value) value)) possible_values))
                :raw-list?         (= mode :create)
                :label-fn          (fn [item] (if (= mode :create) (first item) (:id item)))
                :key-fn            (fn [item idx] (str (first item) "-" (second item) "-" idx))
                :field-id          id
                :field             id
                :name              id
                :edit-permissions? true
                :mapped-field?     true
                :issue_type_id    (:issue_type_id (or current-bugtracker-project bugtracker-data))}))
           (if (= mode :create)
             (normalize-jira-data {:bugtracker-data bugtracker-data
                                   :current-bugtracker-project current-bugtracker-project})
                   ; edit mode
             (:data bugtracker-data)))
      (map (fn [bugtracker-field]
             (let [field-id (keyword (:id bugtracker-field))
                   field-label (keyword (:name bugtracker-field))
                   required (:required bugtracker-field)
                   possible-values (:possible_values bugtracker-field)
                   field_format (if (keyword? (:field_format bugtracker-field)) (:field_format bugtracker-field) (keyword (:field_format bugtracker-field)))
                   field_type (if (= field_format :datetime) :date-time field_format)
                   read-only? (and (:disabled bugtracker-field) (nil? (:error bugtracker-field)))]
               ;; TODO: required in clickup, but not mapped in PT ? - seems to be paid option
               {:field-name         field-id
                :field-id           field-id
                :name               field-id
                :label (if read-only?
                         [:span (str/capitalize (name field-label)) " "
                          [common-tooltip/info-circle-tooltip
                           (get-translation "entity.bugtracker.field.read-only-tooltip"
                                            "This field is an Azure DevOps read-only")
                           {:class "bugtracker-readonly-icon"}]]
                         (str/capitalize (name field-label)))
                :edit-permissions? true
                :read-only? read-only?
                :disabled? (some? (:error bugtracker-field))
                :mapped-field?     true
                :type field_type
                :mandatory required
                :validators (when required [:practitest.frontend.components.forms/string-not-blank])
                :possible-values  (when possible-values (map first possible-values))}))
           (if (= mode :create)
             (get-bugtracker-required-fields bugtracker-data)
                       ;edit mode
             (:data bugtracker-data))))))

(defn get-bugtracker-fields-config []
  (let [bugtracker-data @(re-frame/subscribe [::bugtracker-fields-info])
        {:keys [subtask? supports-linked-issue?]} (get-bugtracker-related-fields bugtracker-data)
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        link-issue-disabled? (nil? @(re-frame/subscribe [::get-bugtracker-field-value :issuelinks]))
        linked-issue-key @(re-frame/subscribe [::get-bugtracker-field-value :linkedIssueKey])
        fields [(when subtask? {:label             "Issue Parent ID"
                                :field             :jira-parent-task
                                :field-id          :jira-parent-task
                                :field-name        :jira-parent-task
                                :name              :jira-parent-task
                                :mandatory?        true
                                :validators        (forms/get-mandatory-validator :text)
                                :type              :text
                                :edit-permissions? true})
                {:label             "Name"
                 :field             :jira-issue-name
                 :field-id          :jira-issue-name
                 :field-name        :jira-issue-name
                 :name              :jira-issue-name
                 :mandatory?        true
                 :validators        (forms/get-mandatory-validator :text)
                 :type              :text
                 :edit-permissions? true}
                {:label             "Description"
                 :field             :jira-issue-description
                 :field-id          :jira-issue-description
                 :field-name        :jira-issue-description
                 :name              :jira-issue-description
                 :type              :memo
                 :mandatory?        true
                 :validators        (forms/get-mandatory-validator :text)
                 :edit-permissions? true}
                (when supports-linked-issue? {:label             "Linked Issue Relation"
                                              :field             :issuelinks
                                              :field-id          :issuelinks
                                              :field-name        :issuelinks
                                              :name              :issuelinks
                                              :mandatory? false
                                              :type :list
                                              :possible-values (:allowedValues (get-jira-field-by bugtracker-data "Linked Issue Relation" :name))
                                              :label-fn (fn [item] (get-in item [:id :value]))
                                              :key-fn (fn [item idx] (str (:issueLinkType item) "-" (:value item) "-" idx))
                                              :edit-permissions? true})
                (when supports-linked-issue? {:label             "Linked Issue Key"
                                              :field             :linkedIssueKey
                                              :field-id          :linkedIssueKey
                                              :field-name        :linkedIssueKey
                                              :name              :linkedIssueKey
                                              :disabled?         link-issue-disabled?
                                              :placeholder       (when link-issue-disabled? "Select relation first")
                                              :mandatory? false
                                              :type :text
                                              :edit-permissions? true})]]
      ; clear Linked Issue Key field if Linked Issue Relation field is cleared
    (when (and link-issue-disabled? (not (nil? linked-issue-key)))
      (re-frame/dispatch [::forms/set-field-state bugtracker-form-id :linkedIssueKey {:value nil}]))
    (if (= mode :edit) [] fields)))

(defn bugtracker-fields-config [db]
  (let [current-project (subs/get-current-project db)
        bugtracker-data (get-bugtracker-fields-info db)
        jira? (bugtrackers/jira? (bugtracker-type current-project))
        current-bugtracker-project-key (if jira? :jira_project :bugtracker_project)
        {current-bugtracker-project current-bugtracker-project-key} bugtracker-data]
    (remove nil? (into (get-bugtracker-fields-config)
                       (get-bugtracker-mapped-fields-config {:bugtracker-data bugtracker-data
                                                             :bugtracker-name (get-bugtracker-name)
                                                             :current-bugtracker-project current-bugtracker-project})))))

(re-frame/reg-sub
 ::bugtracker-fields-config
 (fn [db]
   (bugtracker-fields-config db)))

(re-frame/reg-sub
 ::bugtracker-field-props
 (fn [_db [_ field-id]]
   (let [bugtracker-fields-config @(re-frame/subscribe [::bugtracker-fields-config])]
     (first (filter #(= (keyword (:field-id %)) field-id) bugtracker-fields-config)))))

(defn format-project-name
  [jira? mode project]
  (if jira?
    (if (= mode :create)
      ;; In create mode with split fields, show only project name
      (:jira_proj_name project)
      ;; In edit mode, show project + issue type (old behavior)
      (str (:jira_proj_name project) " - " (:issue_name project)))
    ;; Clickup for now
    (if-let [space (:space_name project)]
      (str space " - " (:name project))
      (:name project))))

(defn disabled-project-selector-item
  "Custom selector item renderer that disables items with :entity_disabled true"
  [{:keys [item label-fn] :as props}]
  (let [disabled? (:entity_disabled item)]
    (if disabled?
      [tooltip/mux-tooltip
       {}
       (get-translation "entity.dropdown.sync_disabled_tooltip"
                        "This option has been disabled by your administrator's settings.")
       (fn [tooltip-props]
         [:div.selector-item.disabled
          tooltip-props
          [:span.selector-item-text (label-fn item)]])]
      [selector2/default-selector-item props])))

(defn- get-bugtracker-fields-props [db]
  (let [bugtracker-data (get-bugtracker-fields-info db)
        current-project (subs/get-current-project db)
        jira? (bugtrackers/jira? (bugtracker-type current-project))
        clickup-sync-all-lists? (boolean (:sync_all_lists bugtracker-data))
        form-state (get-in db [:form-states2 bugtracker-form-id])
        mode (or (get-in form-state [:bugtracker-modal :mode]) :edit)
        current-bugtracker-project-key (if jira? :jira_project :bugtracker_project)
        bugtracker-projects-key 	(if jira? :jira_options :bugtracker_projects)
        {bugtracker-projects bugtracker-projects-key current-bugtracker-project current-bugtracker-project-key} bugtracker-data
        ;; For Jira in create mode, use unique projects only (not project+issue_type combinations)
        projects-for-selector (if (and jira? (= mode :create))
                                (bugtrackers/get-unique-jira-projects bugtracker-projects)
                                bugtracker-projects)
        ;; ClickUp split: don't fall back to a default Space - its lists/entity-types are
        ;; only fetched on explicit selection (::change-bugtracker-project), so a silently
        ;; auto-selected default would leave the List/Entity Type fields stuck empty.
        project-value (or (:value (get-in form-state [:fields :jira-project]))
                          (when-not clickup-sync-all-lists? current-bugtracker-project))
        fields-config (bugtracker-fields-config db)]
    (into (if (= mode :create)
            [{:label           (get-project-label clickup-sync-all-lists?)
              :field-id        :jira-project
              :field-name      :jira-project
              :base-control?   true
              :value           project-value
              :mandatory?      true
              :possible-values projects-for-selector
              :key-fn          (fn [project] (cond
                                               (and jira? (= mode :create)) (:p_id project)
                                               clickup-sync-all-lists?      (:id project)
                                               :else (str (:p_id project) "-" (if jira? (:issue_type_id project) (:id project)))))
              :label-fn        (reagent/partial format-project-name jira? mode)
              :selector-item-renderer disabled-project-selector-item
              :on-change       (fn [new-value]
                                 (when-not (= new-value project-value)
                                   (re-frame/dispatch [::change-bugtracker-project new-value jira?])))
              :edit-permissions? true}]
            [])
          fields-config)))

(re-frame/reg-sub
 ::bugtracker-fields-props
 (fn [db]
   (get-bugtracker-fields-props db)))

(defn- get-bugtracker-cache-key [bugtracker-project bugtracker-name]
  (case bugtracker-name
    :jira (str (:p_id bugtracker-project) " - " (:issue_type_id bugtracker-project))
    :click-up (str (:id bugtracker-project) " - " (:p_id bugtracker-project))
    (str (:id bugtracker-project))))

;; this function caches current bugtracker data into :bugtracker-cached-data field
;; we cache all current bugtracker-fields-data and fields except mapped fields values
(defn- cache-bugtracker-data [db]
  (let [bugtracker-data (get-bugtracker-fields-info db)
        bugtracker-fields-props (get-bugtracker-fields-props db)
        mapped-fields (mapv :field-name (filter (fn [field-props] (:mapped-field? field-props)) bugtracker-fields-props))
        fields (get-in db [:form-states2 bugtracker-form-id :fields])
        ;; reset values for mapped fields
        fields-for-cache (merge fields (into {} (map (fn [field-name] [field-name {:value nil}]) mapped-fields)))
        bugtracker-name (get-bugtracker-name)
        bugtracker-project-key (if (= bugtracker-name :jira) :jira_project :bugtracker_project)
        bugtracker-project (get-in bugtracker-data [bugtracker-project-key])
        bugtracker-cache-key (get-bugtracker-cache-key bugtracker-project bugtracker-name)]
    (-> db
        (assoc-in [:form-states2 bugtracker-form-id :bugtracker-cached-data (keyword bugtracker-cache-key) :bugtracker-fields-data] bugtracker-data)
        (assoc-in [:form-states2 bugtracker-form-id :bugtracker-cached-data (keyword bugtracker-cache-key) :fields] fields-for-cache))))

(re-frame/reg-event-db
  ::get-data-from-cache
  (fn [db [_ cached-data]]
    (-> db
        (assoc-in [:form-states2 bugtracker-form-id :bugtracker-fields-data] (:bugtracker-fields-data cached-data))
        (assoc-in [:form-states2 bugtracker-form-id :fields] (:fields cached-data)))))

(re-frame/reg-event-fx
 ::change-bugtracker-project
 (fn [{db :db} [_ new-project jira?]]
   (let [project-field (if jira? :jira_project :bugtracker_project)
         mode (or (get-in db [:form-states2 bugtracker-form-id :bugtracker-modal :mode]) :edit)
         bugtracker-name (get-bugtracker-name)
         ;; For Jira in create mode with split fields, don't use caching or fetch yet
         ;; We'll fetch when issue type is selected
         jira-create-split? (and jira? (= mode :create))
         ;; For ClickUp with "sync all lists" on, this field is the Space - don't fetch
         ;; project-scoped data yet, fetch that space's lists instead (::clickup-list-field
         ;; handles the dependent List selection, mirroring the Jira split pattern above).
         clickup-split? (and (= bugtracker-name :click-up) (= mode :create)
                             (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data :sync_all_lists]))
         skip-project-fetch? (or jira-create-split? clickup-split?)
         bugtracker-cache-key (when-not skip-project-fetch?
                                (get-bugtracker-cache-key new-project bugtracker-name))
         cached-data (when bugtracker-cache-key
                       (get-in db [:form-states2 bugtracker-form-id :bugtracker-cached-data (keyword bugtracker-cache-key)]))]
     {:db (-> db
              ;; Cache current bugtracker data (skip for Jira/ClickUp create mode with split fields)
              (cond-> (not skip-project-fetch?) cache-bugtracker-data)
              ;; change project in bugtracker-fields-data
              (update-in [:form-states2 bugtracker-form-id :bugtracker-fields-data]
                         (fn [bugtracker-data]
                           (assoc bugtracker-data project-field new-project)))
              ;; For Jira/ClickUp create mode with split fields: store project/space in form
              ;; field and clear the dependent fields for whichever split is active
              (cond->
                skip-project-fetch?
                (-> (assoc-in [:form-states2 bugtracker-form-id :fields :jira-project :value] new-project)
                    (assoc-in [:form-states2 bugtracker-form-id :fields :jira-project :dirty?] true))
                jira-create-split?
                (-> (assoc-in [:form-states2 bugtracker-form-id :fields :jira-issue-type :value] nil)
                    (assoc-in [:form-states2 bugtracker-form-id :fields :jira-parent-task :value] nil))
                clickup-split?
                (-> (assoc-in [:form-states2 bugtracker-form-id :fields :clickup-list :value] nil)
                    (assoc-in [:form-states2 bugtracker-form-id :fields :clickup-entity-type :value] nil))))
      ;; Only fetch project-scoped data for non-split bugtrackers/modes; ClickUp split fetches
      ;; the space's lists instead (see ::fetch-clickup-lists-for-space).
      :fx (cond
            jira-create-split? nil
            clickup-split? [[:dispatch [::fetch-clickup-lists-for-space new-project]]]
            cached-data [[:dispatch [::get-data-from-cache cached-data]]]
            :else [[:dispatch [::fetch-bugtracker-create-info new-project]]])})))

(re-frame/reg-event-fx
 ::fetch-clickup-lists-for-space
 (fn [{db :db} [_ space]]
   {:db (update db :progress conj ::fetch-bugtracker-fields-info)
    :http-xhrio
    {:method          :get
     :uri             (broutes/get-url "bugtracker_settings#bugtracker_clickup_lists"
                                       {:project_id (subs/get-current-project-id db)})
     :vec-strategy    :rails
     :params          {:space_ids [(:id space)]}
     :format          (ajax/json-request-format)
     :response-format (ajax/json-response-format {:keywords? true})
     :on-success      [::got-clickup-lists-for-space]
     :on-failure      [::fetch-bugtracker-fields-error]}}))

(re-frame/reg-event-db
 ::got-clickup-lists-for-space
 (fn [db [_ response]]
   (-> db
       (update :progress disj ::fetch-bugtracker-fields-info)
       (update-in [:form-states2 bugtracker-form-id :bugtracker-fields-data]
                  (fn [bugtracker-data]
                    (merge bugtracker-data (select-keys response [:lists_by_space :entity_types])))))))

(re-frame/reg-event-fx
 ::clickup-list-changed
 (fn [{db :db} [_ new-list]]
   {:db (-> db
            (assoc-in [:form-states2 bugtracker-form-id :fields :clickup-list :value] new-list)
            (assoc-in [:form-states2 bugtracker-form-id :fields :clickup-list :dirty?] true)
            (assoc-in [:form-states2 bugtracker-form-id :fields :clickup-entity-type :value] nil))}))

(re-frame/reg-event-fx
 ::jira-issue-type-changed
 (fn [{db :db} [_ new-issue-type]]
   (let [selected-project (get-bugtracker-field-value db :jira-project)
         ;; Construct a combined project+issue_type object for backend fetch
         combined-project {:p_id (:p_id selected-project)
                          :jira_proj_name (:jira_proj_name selected-project)
                          :jira_proj_key (:jira_proj_key selected-project)
                          :issue_type_id (:issue_type_id new-issue-type)
                          :issue_name (:issue_name new-issue-type)
                          :is_sub_task (:is_sub_task new-issue-type)}
         bugtracker-name (get-bugtracker-name)
         bugtracker-cache-key (get-bugtracker-cache-key combined-project bugtracker-name)
         cached-data (get-in db [:form-states2 bugtracker-form-id :bugtracker-cached-data (keyword bugtracker-cache-key)])]
     {:db (-> db
              ;; Update issue type value
              (assoc-in [:form-states2 bugtracker-form-id :fields :jira-issue-type :value] new-issue-type)
              (assoc-in [:form-states2 bugtracker-form-id :fields :jira-issue-type :dirty?] true)
              ;; Update jira_project in bugtracker-fields-data with combined project+issue_type
              (assoc-in [:form-states2 bugtracker-form-id :bugtracker-fields-data :jira_project] combined-project)
              ;; Clear parent field if switching away from sub-task
              (cond->
                (not= "true" (:is_sub_task new-issue-type))
                (assoc-in [:form-states2 bugtracker-form-id :fields :jira-parent-task :value] nil)))
      ;; Fetch required fields for this project+issue_type combination
      :fx (if cached-data
            [[:dispatch [::get-data-from-cache cached-data]]]
            [[:dispatch [::fetch-bugtracker-create-info combined-project]]])})))

(re-frame/reg-sub
 ::get-modal-error-panel
 (fn [db]
   (get-in db [:form-states2 bugtracker-form-id :bugtracker-modal :error-panel])))

(re-frame/reg-event-db
 ::set-modal-error-panel!
 (fn [db [_ show response-error]]
   (set-modal-error-panel! db show response-error)))

(defn- error-panel []
  (reagent/create-class
   {:component-did-update (fn [this]
                            (when-let [node (reagent-dom/dom-node this)]
                              (.scrollIntoView node (clj->js {:behavior "smooth" :block "end"}))))
    :reagent-render
    (fn []
      (let [{:keys [show-error-panel response-error]} @(re-frame/subscribe [::get-modal-error-panel])]
        (when show-error-panel [:div.modal-error-panel {:dangerouslySetInnerHTML {:__html (str response-error)}}])))}))

(re-frame/reg-sub
 ::get-attachments-to-show
 (fn [db]
   (get-in db [:form-states2 bugtracker-form-id :bugtracker-modal :attachments-panel :attachments-to-show])))

(re-frame/reg-event-db
 ::set-attachments-to-show!
 (fn [db [_ attachments]]
   (-> db
       (assoc-in [:form-states2 bugtracker-form-id :bugtracker-modal :attachments-panel :attachments-to-show] attachments))))

(defn- et-attachments-panel []
  (let [attachments-to-show @(re-frame/subscribe [::get-attachments-to-show])]
    [:div.modal-attachments-panel
     (doall
       (map (fn [{:keys [id name size type url] :as attachment}]
              (with-meta
                [:div.modal-attachment
                 [:div.modal-attachment-description.ellipsis
                  [:a {:href url
                       :target "_blank"
                       :title url}
                   (str name " | " (js/parseFloat (.toFixed (/ size 1024) 2)) "k | " type)]]
                 [buttons/icon-button
                  {:on-click (fn []
                               (let [new-attachments-to-show (remove #(= (:id %) id) attachments-to-show)]
                                 (re-frame/dispatch [::set-attachments-to-show! new-attachments-to-show])))
                   :icon [icons/mux-trash]}]]
                {:key id}))
            attachments-to-show))]))

(defn- attachments-panel []
  (let [attachments-to-show @(re-frame/subscribe [::get-attachments-to-show])
        jira? (bugtrackers/jira? (bugtracker-type @(re-frame/subscribe [::subs/current-project])))]
    ;; for now only jira supports upload attachments
    (when (and jira? (seq attachments-to-show))
      [:div.bugtracker-attachments-section
       [:div.bugtracker-attachments-title (get-translation "testset.tabs.attachments" "Attachments")]
       [et-attachments-panel]])))

(defn- modal-header
  [mode]
  (let [bugtracker (bugtrackers/get-label (bugtracker-type @(re-frame/subscribe [::subs/current-project])))]
    [:div.bugtracker-fields-header
     [keyboard-shortcuts/enable-scope {:shortcuts-type :modal}]
     [:div.bugtracker-fields-header-icon [bugtracker-logo]]
     [:div.bugtracker-fields-header-text
      [:span.bugtracker-fields-header-title
       (format-translation
        (case mode
          :edit (get-translation "entity.bugtracker.additional-fields.edit" "%{bugtracker} fields edit")
          :create (get-translation "entity.bugtracker.create" "Create issue in %{bugtracker}"))
        {:bugtracker bugtracker})]
      (when (= mode :create)
        [:span.bugtracker-fields-header-subtitle
         (format-translation
          (get-translation "issue.bugtracker.create.subtitle" "Create an issue in %{bugtracker} synced to PractiTest")
          {:bugtracker bugtracker})])]]))

(defn- render-bugtracker-fields [fields-props]
  (doall (map (fn [props]
                (with-meta [fields3/build-field props bugtracker-form-props]
                  {:key (:field-id props)}))
              fields-props)))

(defn- bugtracker-project-field []
  (let [bugtracker-fields-props @(re-frame/subscribe [::bugtracker-fields-props])
        bugtracker-projects-field (first bugtracker-fields-props)
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        create? (= mode :create)]
    (when create? [:<> [fields3/base-list-control
                        (merge bugtracker-form-props bugtracker-projects-field)]])))

(defn- bugtracker-unmapped-fields []
  (let [bugtracker-fields-props @(re-frame/subscribe [::bugtracker-fields-props])
        not-mapped-fields-props (vec (remove (fn [field-props] (or (:mapped-field? field-props)
                                                                   (:base-control? field-props))) bugtracker-fields-props))]
    [:<> (render-bugtracker-fields not-mapped-fields-props)]))

(defn- bugtracker-mapped-fields []
  (let [bugtracker-fields-props @(re-frame/subscribe [::bugtracker-fields-props])
        mapped-fields-props (vec (filter (fn [field-props] (:mapped-field? field-props)) bugtracker-fields-props))]
    [:<> (render-bugtracker-fields mapped-fields-props)]))

(defn- clickup-list-field []
  (let [bugtracker-name (get-bugtracker-name)
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        sync-all-lists? @(re-frame/subscribe [::clickup-sync-all-lists?])
        selected-space @(re-frame/subscribe [::get-bugtracker-field-value :jira-project])
        space-id (:id selected-space)
        available-lists @(re-frame/subscribe [::get-clickup-lists-for-space space-id])
        selected-list @(re-frame/subscribe [::get-bugtracker-field-value :clickup-list])]
    (when (and (= bugtracker-name :click-up) (= mode :create) sync-all-lists? space-id)
      [fields3/base-list-control
       (merge bugtracker-form-props
              {:label           (get-translation "clickup.list.label" "ClickUp List")
               :field-id        :clickup-list
               :field-name      :clickup-list
               :value           selected-list
               :mandatory?      true
               :possible-values (or available-lists [])
               :key-fn          :id
               :label-fn        :name
               :on-change       (fn [new-value]
                                  (re-frame/dispatch [::clickup-list-changed new-value]))
               :edit-permissions? true})])))

(defn- clickup-entity-type-field []
  (let [bugtracker-name (get-bugtracker-name)
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        bugtracker-data @(re-frame/subscribe [::bugtracker-fields-info])
        sync-all-lists? @(re-frame/subscribe [::clickup-sync-all-lists?])
        selected-project-from-field @(re-frame/subscribe [::get-bugtracker-field-value :jira-project])
        selected-list-from-field @(re-frame/subscribe [::get-bugtracker-field-value :clickup-list])
        ;; When sync-all-lists is on, :jira-project holds the Space - resolve entity types
        ;; against the selected List field instead. Otherwise unchanged: form field OR
        ;; bugtracker_project from backend (same logic as the project dropdown).
        selected-project (if sync-all-lists?
                          selected-list-from-field
                          (or selected-project-from-field (:bugtracker_project bugtracker-data)))
        project-id (:id selected-project)
        entity-types @(re-frame/subscribe [::get-entity-types-for-list project-id])
        selected-entity-type @(re-frame/subscribe [::get-bugtracker-field-value :clickup-entity-type])]
    (when (and (= bugtracker-name :click-up) (= mode :create)
               (or (not sync-all-lists?) selected-list-from-field))
      (when (and (seq entity-types) (nil? selected-entity-type))
        (let [bug-type (first (filter #(= "bug" (some-> % str/lower-case)) entity-types))
              default-type (or bug-type (first entity-types))]
          (re-frame/dispatch [::forms/set-field-state bugtracker-form-id :clickup-entity-type
                              {:value default-type :dirty? true}])))
      [fields3/base-list-control
       (merge bugtracker-form-props
              {:label           (get-translation "clickup.entity_type.label" "Entity Type")
               :field-id        :clickup-entity-type
               :field-name      :clickup-entity-type
               :value           selected-entity-type
               :mandatory?      true
               :possible-values (or entity-types [])
               :key-fn          identity
               :label-fn        #(when %
                                   (if (str/includes? % "_")
                                     (->> (str/split (str/replace % "_" " ") #"\s+")
                                          (map str/capitalize)
                                          (str/join " "))
                                     %))
               :on-change       (fn [new-value]
                                  (re-frame/dispatch [::forms/set-field-state bugtracker-form-id :clickup-entity-type
                                                     {:value new-value
                                                      :dirty? true}]))
               :edit-permissions? true})])))

(defn- clickup-parent-task-field []
  (let [bugtracker-name (get-bugtracker-name)
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        value @(re-frame/subscribe [::get-bugtracker-field-value :clickup-parent-task])]
    (when (and (= bugtracker-name :click-up)
               (= mode :create))
      [fields3/base-control
       (merge bugtracker-form-props
              {:label           [:span
                                 (get-translation "clickup.parent_task.label" "Parent Task ID")
                                 " "
                                 [common-tooltip/info-circle-tooltip
                                  (get-translation "clickup.parent_task.help" "Optionally link this task to a parent task.")]]
               :field-id        :clickup-parent-task
               :field-name      :clickup-parent-task
               :value           value
               :mandatory?      false
               :edit-permissions? true})
       ;; Editing component - text input with form props
       [fields3/text-input (merge bugtracker-form-props
                                  {:field-id :clickup-parent-task
                                   :placeholder (get-translation "clickup.parent_task.placeholder" "Please add parent Task ID")
                                   :value (or value "")
                                   :type :text})]
       ;; Readonly component
       (fn [value] [:span value])])))

(re-frame/reg-event-db
 ::azure-work-item-type-changed
 (fn [db [_ new-type]]
   (-> db
       (assoc-in [:form-states2 bugtracker-form-id :fields :azure-work-item-type :value] new-type)
       (assoc-in [:form-states2 bugtracker-form-id :fields :azure-work-item-type :dirty?] true)
       (cond->
         (not (:supports_parent new-type))
         (assoc-in [:form-states2 bugtracker-form-id :fields :azure-parent-id :value] nil)))))

(defn- azure-work-item-type-field []
  (let [bugtracker-name (get-bugtracker-name)
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        bugtracker-data @(re-frame/subscribe [::bugtracker-fields-info])
        selected-project (or @(re-frame/subscribe [::get-bugtracker-field-value :jira-project])
                             (:bugtracker_project bugtracker-data))
        project-id (:id selected-project)
        work-item-types @(re-frame/subscribe [::get-work-item-types-for-azure-project project-id])
        selected-wit @(re-frame/subscribe [::get-bugtracker-field-value :azure-work-item-type])]
    (when (and (= bugtracker-name :azuredevops) (= mode :create))
      (when (and (seq work-item-types) (nil? selected-wit))
        (let [bug-type (first (filter #(= "bug" (some-> % :name str/lower-case)) work-item-types))
              default-type (or bug-type (first work-item-types))]
          (re-frame/dispatch [::azure-work-item-type-changed default-type])))
      [fields3/base-list-control
       (merge bugtracker-form-props
              {:label           (get-translation "azure.work_item_type.label" "Work Item Type")
               :field-id        :azure-work-item-type
               :field-name      :azure-work-item-type
               :value           selected-wit
               :mandatory?      true
               :possible-values (or work-item-types [])
               :key-fn          :id
               :label-fn        :name
               :on-change       (fn [new-value]
                                  (re-frame/dispatch [::azure-work-item-type-changed new-value]))
               :edit-permissions? true})])))

(defn- azure-parent-field []
  (let [bugtracker-name (get-bugtracker-name)
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        selected-wit @(re-frame/subscribe [::get-bugtracker-field-value :azure-work-item-type])
        value @(re-frame/subscribe [::get-bugtracker-field-value :azure-parent-id])]
    (when (and (= bugtracker-name :azuredevops) (= mode :create) (:supports_parent selected-wit))
      [fields3/base-control
       (merge bugtracker-form-props
              {:label           [:span
                                 (get-translation "azure.parent_id.label" "Parent Work Item ID")
                                 " "
                                 [common-tooltip/info-circle-tooltip
                                  (get-translation "azure.parent_id.help" "Enter the ID of the parent work item.")]]
               :field-id        :azure-parent-id
               :field-name      :azure-parent-id
               :value           value
               :mandatory?      false
               :edit-permissions? true})
       [fields3/text-input (merge bugtracker-form-props
                                  {:field-id    :azure-parent-id
                                   :placeholder (get-translation "azure.parent_id.placeholder"
                                                                 "Enter parent work item ID")
                                   :value       (or value "")
                                   :type        :text})]
       (fn [value] [:span value])])))

(re-frame/reg-event-db
 ::requirement-changed
 (fn [db [_ new-value]]
   (assoc-in db [:form-states2 bugtracker-form-id :fields :requirement-id :value] new-value)))

(defn- requirement-field []
  (let [mode @(re-frame/subscribe [::bugtracker-modal-mode])
        create-modal-params @(re-frame/subscribe [::create-modal-params])
        ;; Only relevant when the issue is being reported from a run step or ET annotation -
        ;; not when converting/creating an issue from the Issue form itself.
        run-or-et-context? (some create-modal-params [:step_run_id :et_annotation_id])
        traced-requirements @(re-frame/subscribe [::traced-requirements])
        value @(re-frame/subscribe [::get-bugtracker-field-value :requirement-id])
        no-traced-requirements? (empty? traced-requirements)]
    (when (and (= mode :create) run-or-et-context?)
      [fields3/base-list-control
       (merge bugtracker-form-props
              {:label             [:span
                                   (get-translation "issue.bugtracker.requirement.label"
                                                     "Link Issue to PractiTest Requirements")
                                   " "
                                   [common-tooltip/info-circle-tooltip
                                    (get-translation "issue.bugtracker.requirement.help"
                                                      "Link the created issue to a requirement traced back to this run.")]]
               :container-class   "requirement-traceability-field"
               :field-id          :requirement-id
               :field-name        :requirement-id
               :value             value
               :mandatory?        false
               :disabled?         no-traced-requirements?
               :placeholder       (when no-traced-requirements?
                                    (get-translation "issue.bugtracker.requirement.empty"
                                                      "No Traced Requirements are found"))
               :possible-values   traced-requirements
               :key-fn            :id
               :label-fn          (fn [requirement] (str (:display_id requirement) " - " (:name requirement)))
               :on-change         (fn [new-value]
                                    (mixpanel/track "Form Actions" {"Action" "Select Traced Requirement"})
                                    (re-frame/dispatch [::requirement-changed new-value]))
               :edit-permissions? true})])))

(defn- jira-issue-type-field []
  (let [bugtracker-name (get-bugtracker-name)
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        selected-project-id @(re-frame/subscribe [::jira-selected-project-id])
        issue-types @(re-frame/subscribe [::get-issue-types-for-jira-project selected-project-id])
        selected-issue-type @(re-frame/subscribe [::get-bugtracker-field-value :jira-issue-type])]
    (when (and (= bugtracker-name :jira) (= mode :create) selected-project-id)
      (when (and (seq issue-types) (nil? selected-issue-type))
        (let [bug-type (first (filter #(= "bug" (some-> % :issue_name str/lower-case)) issue-types))
              default-type (or bug-type (first issue-types))]
          (re-frame/dispatch [::jira-issue-type-changed default-type])))
      [fields3/base-list-control
       (merge bugtracker-form-props
              {:label           (get-translation "jira.issue_type.label" "Issue Type")
               :field-id        :jira-issue-type
               :field-name      :jira-issue-type
               :value           selected-issue-type
               :mandatory?      true
               :possible-values (or issue-types [])
               :key-fn          :issue_type_id
               :label-fn        :issue_name
               :on-change       (fn [new-value]
                                  (re-frame/dispatch [::jira-issue-type-changed new-value]))
               :edit-permissions? true})])))

(defn- modal-body [{:keys [loading?]}]
  [:div.create-modal-fields-container.bugtracker-modal-body-container
   {:ref #(reset! (:form-ref bugtracker-form-props) %)}
   [bugtracker-project-field]
   [clickup-list-field]
   [jira-issue-type-field]
   [clickup-entity-type-field]
   [clickup-parent-task-field]
   [azure-work-item-type-field]
   [azure-parent-field]
   [bugtracker-unmapped-fields]
   (if loading?
     [:div.inner-spinner-holder
      [:div [spinners2/mux-spinner {:size :medium}]]
      [:div.inner-spinner-text (get-translation "issue.bugtracker.modal.loading" "Loading ... Please Wait")]]
     [bugtracker-mapped-fields])
   [requirement-field]
   [error-panel]
   [attachments-panel]])

(defn- modal-on-save-click-handler [{:keys [mode jira?]}]
  (let [form-state (forms/force-form-validation! bugtracker-form-props)
        form-errors (forms/collect-form-errors form-state)
        first-error-field (ffirst form-errors)
        {:keys [get-field-props get-field-state]} bugtracker-form-props
        first-field-props @(get-field-props first-error-field)
        first-field-state @(get-field-state first-error-field)
        form-ref (:form-ref bugtracker-form-props)]
    (if-not (seq form-errors)
      (do
        ;; Clear and close error panel
        (re-frame/dispatch [::set-modal-error-panel! false nil])
        (case mode
          :edit (re-frame/dispatch [::save-bugtracker-fields! bugtracker-form-id])
          :create (re-frame/dispatch [::create-bugtracker-issue! jira? bugtracker-form-id])))
      (forms3/scroll-to-field form-ref first-field-props first-field-state))))

(defn- modal-footer [{:keys [mode saving? loading? open?]}]
  (let [jira? (bugtrackers/jira? (bugtracker-type @(re-frame/subscribe [::subs/current-project])))
        bugtracker-errors @(re-frame/subscribe [::bugtracker-fields-errors])]
    [:div.bugtracker-fields-footer
     [buttons/button
      {:text      (get-translation "generic.button.cancel" "Cancel")
       ;; Can't cancel while saving for now
       :disabled? (or saving? (not open?))
       :on-click  (fn [_e] (re-frame/dispatch [::hide-bugtracker-modal!]))}]

     (when-not bugtracker-errors
       (let [on-click (fn [_e] (modal-on-save-click-handler {:mode mode :jira? jira?}))]
         [:<>
          [keyboard-shortcuts/add-shortcut {:shortcut [:modal :save] :handler on-click}]
          [buttons/button
           {:primary?  true
            :disabled? (or saving? loading? (not open?))
            :text (case mode
                    :edit (get-translation "generic.button.save" "Save")
                    :create (get-translation "issue.bugtracker.modal.create" "Create Issue"))
            :icon [icons/mux-plus-circle]
            :end-icon (when saving? [spinners2/mux-spinner {:size :small :inside? true}])
            :class    "save-button"
            :on-click on-click}]]))]))

(defn bugtracker-integration-modal []
  (let [open? @(re-frame/subscribe [::show-bugtracker-modal?])
        mode @(re-frame/subscribe [::bugtracker-modal-mode])
        loading? @(re-frame/subscribe [::subs/progress? ::fetch-bugtracker-fields-info])
        saving? @(re-frame/subscribe [::subs/progress? ::save-bugtracker-fields!])]
    [modal/bootstrap-modal
     {:class            "bugtracker-integration-modal"
      :is-open?         open?
      :on-request-close #(re-frame/dispatch [::hide-bugtracker-modal!])
      :close-button?    (not saving?)
      :centered?        true}

       ;; Header
     [modal-header mode]
       ;; Body
     [modal-body {:loading? loading?}]
       ;; Footer
     [modal-footer {:mode mode :saving? saving? :loading? loading? :open? open?}]]))

(defn- convert-bugtracker-list-value
  [bugtracker-field value]
  (let [bugtracker-value (first (filter #(if (vector? %)
                                           (= (first %) value)
                                           (= % value))
                                        (:possible_values bugtracker-field)))]
    (if (vector? bugtracker-value)
        ;; Return id here
      (second bugtracker-value)
      bugtracker-value)))

(defn- convert-bugtracker-field
  [bugtracker-fields field value]
  (if (contains? bugtracker-fields field)
    (let [bugtracker-field (get bugtracker-fields field)
          format (:field_format bugtracker-field)]
      (cond
        (= "list" format)
          ;; Process list values (used in e.g. jira sprint)
        (convert-bugtracker-list-value bugtracker-field value)

          ;; sigh
        (= "multilist" format)
        (map (partial convert-bugtracker-list-value bugtracker-field)
             (fields/try-parse-multilist-value value))
        :else
        value))
    (do
      (js/console.warn "non-bugtracker field supplied to bugtracker field converter !" field)
      value)))

(re-frame/reg-event-fx
 ::save-bugtracker-fields!
 (fn [{db :db} [_ bugtracker-form-id]]
   (let [bugtracker-fields (bugtracker-fields (:fields db))
         jira? (bugtrackers/jira? (bugtracker-type (subs/get-current-project db)))
         bugtracker-form-fields (into {}
                                      (map (fn [field]
                                               ;; collect into map
                                             [(keyword (:id field))
                                              field]))
                                      (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data :data]))]
     {:db (update db :progress conj ::save-bugtracker-fields!)
      :http-xhrio
      (let [current-entity (:current-entity db)
            customizable_class (if (= current-entity :issue) "Issue" "Requirement")
            bugtracker-data (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data])
              ;; Collect dirty fields here
            fields (reduce-kv (fn [acc id state]
                                (if (:dirty? state)
                                  (assoc acc id (convert-bugtracker-field bugtracker-form-fields id (:value state)))
                                  acc))
                              {}
                              (get-in db [:form-states2 bugtracker-form-id :fields]))]
        {:method          :post
         :uri             (broutes/get-url (if jira?
                                             "jira_modal#jira_fields_to_update"
                                             "bugtracker_modal#bugtracker_fields_to_update")
                                           {:p_id (subs/get-current-project-id db)})
         :params          (if jira?
                            {:issue_type_id      (:issue_type_id bugtracker-data)
                             :jira_proj_key      (:jira_proj_key bugtracker-data)
                             :jira_issue_key     (:jira_issue_key bugtracker-data)
                             :customizable_class customizable_class
                             :custom             fields}
                            {:bugtracker_issue_key (:bugtracker_issue_key bugtracker-data)
                             :customizable_class   customizable_class
                             :custom               fields})
         :format          (ajax/json-request-format)
         :response-format (ajax/json-response-format {:keywords? true})
         :on-success      [::bugtracker-save-success]
         :on-failure      [::bugtracker-save-failed]})})))

(re-frame/reg-event-fx
 ::bugtracker-save-failed
 (fn [{db :db} [_ response]]
   (let [error-msg (get-in response [:response :error])
         db (update db :progress disj ::save-bugtracker-fields!)]
     (if error-msg
       {:db (set-modal-error-panel! db true error-msg)}
       {:db       db
        :dispatch [::notifications/error2 (get-translation "issue.bugtracker-fields-update-failed"
                                                           "Error saving issue in bugtracker")]}))))

(re-frame/reg-event-fx
 ::bugtracker-save-success
 (fn [{db :db} [_ {:keys [status errors entity] :as response}]]
   (let [db (update db :progress disj ::save-bugtracker-fields!)
         bugtracker (bugtracker-type (subs/get-current-project db))
         current-entity (:current-entity db)
         form-id (get-in entities-metadata [current-entity :form-id])]
     (if (= "ok" status)
       {:db       (-> db
                      (assoc-in [:entity current-entity :current] entity)
                      (assoc-in [:form-states2 issue-form-id :fields] {})
                      (cond-> form-id
                        (forms3/reconcile-form-state form-id false current-entity entity)))
        :dispatch-n [[::hide-bugtracker-modal!]
                     [::notifications/success2 (format-translation
                                                (get-translation "entity.bugtracker.additional-fields.updated"
                                                                 "%{bugtracker} fields were successfully updated")
                                                {:bugtracker (bugtrackers/get-label bugtracker)})]]}

       {:db       db
        :dispatch [::notifications/error2 (format-translation
                                           (get-translation "entity.bugtracker.additional-fields.error-updating"
                                                            "%{bugtracker} was not updated. %{message}")
                                           {:bugtracker (bugtrackers/get-label bugtracker)
                                              ;; It's called "errors", but it's actually a single error
                                            :message    errors})]}))))

  ;; Create maps out of kv pairs skipping nil values
(defn map-without-nils
  [& ms]
  (persistent!
   (reduce
    (fn [acc [k v]]
      (if (some? v)
        (assoc! acc k v)
        acc))
    (transient {})
    (partition-all 2 ms))))

(re-frame/reg-event-fx
 ::create-bugtracker-issue!
 (fn [_ [_ jira? bugtracker-form-id]]
     ;; TODO: fix this mess
   (if jira?
     {:dispatch [::create-jira-issue! bugtracker-form-id]}
     ;; other bugtrackers (ClickUp, AzureDevops)
     {:dispatch [::create-other-issue! bugtracker-form-id]})))

(re-frame/reg-event-fx
 ::create-jira-issue!
 (fn [{db :db} [_ bugtracker-form-id]]
   {:db (update db :progress conj ::save-bugtracker-fields!)
    :http-xhrio
    (let [bugtracker-data (get-bugtracker-fields-info db)
          jira-fields (get-jira-required-fields bugtracker-data)
          ;; Get selected project and issue type from form fields (split fields in create mode)
          selected-project (get-bugtracker-field-value db :jira-project)
          selected-issue-type (get-bugtracker-field-value db :jira-issue-type)
          ;; Use selected values if available (create mode), otherwise use backend data (edit mode)
          jira-project (or selected-project (:jira_project bugtracker-data))
          issue-type-id (or (:issue_type_id selected-issue-type) (:issue_type_id jira-project))

            ;; Collect field values out of form state
          jira-fields-params (reduce
                              (fn [acc {:keys [key] :as field}]
                                (let [field-value (get-in db [:form-states2 bugtracker-form-id :fields (keyword key) :value])
                                      [jira-field-type pt-field-type] (get-jira-field-type field)]
                                  (if field-value
                                    (assoc acc key (cond
                                                       ;; Vectors are supposed to be name/id pairs
                                                     (and
                                                      (contains? #{:sprint :team :epic :user :securitylevel} jira-field-type)
                                                      (not (= :jira_user_list pt-field-type)))
                                                     (last field-value)
                                                     (= :list pt-field-type)
                                                     (first field-value)
                                                     (= :multilist pt-field-type)
                                                       ;; List of identifiers
                                                     (map last field-value)
                                                     :else
                                                     field-value))
                                    acc)))
                              {}
                                ;; issuelinks are handled separately
                              (remove
                               (fn [f]
                                 (or (= (:key f) "issuelinks")
                                     (= (:key f) "linkedIssueKey")))
                               jira-fields))

            ;; Handle jira links
          issue-links-data (when-let [issue-link (get-bugtracker-field-value db :issuelinks)]
                             {:issuelinks     (:name issue-link)
                              :issueLinkType  (:issueLinkType issue-link)
                              :linkedIssueKey (get-bugtracker-field-value db :linkedIssueKey)})
          create-modal-params (get-in db [:form-states2 bugtracker-form-id :params])
          attachments-to-show (get-in db [:form-states2 bugtracker-form-id :bugtracker-modal :attachments-panel :attachments-to-show])

          ;; Legacy backend support - combine project + issue type (matches Old UI param name)
          params (merge (map-without-nils
                         :external_project_issue_type (str (:p_id jira-project) " - " issue-type-id)
                         :bug_name (get-bugtracker-field-value db :jira-issue-name)
                         :bug_description (get-bugtracker-field-value db :jira-issue-description)
                         :issue_parent_id (get-bugtracker-field-value db :jira-parent-task)

                         :jira_modal_field jira-fields-params
                         :issuelinksdata issue-links-data
                         :jira_synced_attachments (str/join ", " (map :id attachments-to-show))
                         :requirement_id (:id (get-bugtracker-field-value db :requirement-id)))
                        create-modal-params)]
      {:method          :post
       :uri             (broutes/get-url "jira_modal#create"
                                         {:p_id (subs/get-current-project-id db)})
       :params          params
       :format          (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success      [::bugtracker-create-success]
       :on-failure      [::bugtracker-save-failed]})}))

(re-frame/reg-event-fx
 ::create-other-issue!
 (fn [{db :db} [_ bugtracker-form-id]]
   {:db (update db :progress conj ::save-bugtracker-fields!)
    :http-xhrio
    (let [current-entity (:current-entity db)
          bugtracker-data (get-bugtracker-fields-info db)
          bugtracker-name (get-bugtracker-name)
          bugtracker-project (:bugtracker_project bugtracker-data)
          bugtracker-form-fields (into {}
                                       (map (fn [field]
                                              ;; collect into map
                                              [(keyword (:id field))
                                               field]))
                                       (get-in db [:form-states2 bugtracker-form-id :bugtracker-fields-data :bugtracker_fields :proj_req_flds]))

          ;; Collect dirty fields here
          fields (reduce-kv (fn [acc id state]
                              (if (:dirty? state)
                                (assoc acc id (convert-bugtracker-field bugtracker-form-fields id (:value state)))
                                acc))
                            {}
                            (get-in db [:form-states2 bugtracker-form-id :fields]))
          create-modal-params (get-in db [:form-states2 bugtracker-form-id :params])
          bugtracker-related-params (case bugtracker-name
                                      :azuredevops {:azure_devops_project_id (:id bugtracker-project)
                                                    :azure_work_item_type_id (:id (get-bugtracker-field-value db :azure-work-item-type))
                                                    :parent_work_item_id (get-bugtracker-field-value db :azure-parent-id)}
                                      :click-up (let [sync-all-lists? (:sync_all_lists bugtracker-data)]
                                                  (cond-> {:click_up_project_id (:id (if sync-all-lists?
                                                                                      (get-bugtracker-field-value db :clickup-list)
                                                                                      bugtracker-project))
                                                           :clickup_entity_type (get-bugtracker-field-value db :clickup-entity-type)
                                                           :parent_task_id      (get-bugtracker-field-value db :clickup-parent-task)}
                                                    sync-all-lists? (assoc :click_up_space_id (:id bugtracker-project))))
                                      {})
          params (-> (map-without-nils
                      :entity_type current-entity
                      :bug_name (get-bugtracker-field-value db :jira-issue-name)
                      :bug_description (get-bugtracker-field-value db :jira-issue-description)
                      :external_project_id (:id bugtracker-project)
                      :jira_modal_field fields
                      :requirement_id (:id (get-bugtracker-field-value db :requirement-id)))
                     (merge bugtracker-related-params)
                     (merge create-modal-params))]

      {:method          :post
       :uri             (broutes/get-url "bugtracker_modal#fail_n_issue_create"
                                         {:p_id (subs/get-current-project-id db)})

         ;;entity_type=issue
        ;click_up_project_id: 901500014236
        ;bug_name: Mail notifications do not send username on mail address
        ;bug_description: 12313
        ;entity_id: 83
        ;jira_modal_field[priority]: 1
        ;jira_modal_field[___f_272]: 23 Apr 2024
        ;jira_modal_field[___f_274]: 2fb765d9-d3d6-400d-b2e8-5f470e33dff6
        ;jira_modal_field[___f_275][]: 82721867
        ;jira_modal_field[___f_275][]: 56493044

       :params          params
       :format          (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success      [::bugtracker-create-success]
       :on-failure      [::bugtracker-save-failed]})}))

(re-frame/reg-event-fx
 ::bugtracker-create-success
 (fn [{db :db} [_ {:keys [errors url modal-msg entity warnings] :as response}]]
   (let [db (update db :progress disj ::save-bugtracker-fields!)
         current-entity (:current-entity db)
         options (:options db)
         ;; create-only? = true when an issue is created from the run form
         ;; and the issue form and the current entity state should not be updated
         create-only? (get-in db [:form-states2 bugtracker-form-id :params :create_only?])
         on-success-events (get-in db [:form-states2 bugtracker-form-id :on-success-events])]
     (if (empty? errors)
       {:db         (-> db
                        (cond-> (not create-only?)
                          (-> (assoc-in [:entity current-entity :current] entity)
                              (assoc-in [:form-states2 issue-form-id :fields] {})
                              (forms3/reconcile-form-state issue-form-id false current-entity entity))))
        :dispatch-n (into (mapv #(conj % entity) on-success-events)
                          (cond-> [[::hide-bugtracker-modal!]
                                   [::notifications/success2 modal-msg {:raw-html? true}]]
                            (seq warnings) (conj [::notifications/warning2 (str/join "; " warnings)])))}

       {:db       (set-modal-error-panel! db true errors)}))))
